import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const TIP_OFFSET = 8;

function getTooltipCoords(rect, placement) {
  switch (placement) {
    case 'above':
      return {
        top: rect.top - TIP_OFFSET,
        left: rect.left + rect.width / 2,
        transform: 'translate(-50%, -100%)',
      };
    case 'right':
      return {
        top: rect.top + rect.height / 2,
        left: rect.right + TIP_OFFSET,
        transform: 'translateY(-50%)',
      };
    case 'left':
      return {
        top: rect.top + rect.height / 2,
        left: rect.left - TIP_OFFSET,
        transform: 'translate(-100%, -50%)',
      };
    default:
      return {
        top: rect.bottom + TIP_OFFSET,
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
      };
  }
}

/** Tooltip moderno do Detalhe OS 2 — fundo claro, seta, light/dark. */
export function OrderDetailsMockHoverTooltip({
  text,
  content,
  children,
  className = '',
  placement = 'above',
  selectable = false,
  wrap = false,
}) {
  const body = content ?? text;
  const anchorRef = useRef(null);
  const [tip, setTip] = useState({ open: false, coords: null });

  const readCoords = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return null;
    return getTooltipCoords(el.getBoundingClientRect(), placement);
  }, [placement]);

  const show = () => {
    if (body == null || body === '') return;
    const coords = readCoords();
    if (!coords) return;
    setTip({ open: true, coords });
  };

  const hide = () => setTip({ open: false, coords: null });

  const refreshCoords = useCallback(() => {
    const coords = readCoords();
    if (!coords) return;
    setTip((prev) => (prev.open ? { open: true, coords } : prev));
  }, [readCoords]);

  useLayoutEffect(() => {
    if (!tip.open) return undefined;
    refreshCoords();
    window.addEventListener('scroll', refreshCoords, true);
    window.addEventListener('resize', refreshCoords);
    return () => {
      window.removeEventListener('scroll', refreshCoords, true);
      window.removeEventListener('resize', refreshCoords);
    };
  }, [tip.open, refreshCoords]);

  const wrapClass = [
    'order-details-mock-tip-wrap',
    selectable ? 'order-details-mock-tip-wrap--selectable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const popoverClass = [
    'order-details-mock-tip-popover',
    wrap ? 'order-details-mock-tip-popover--wrap' : '',
    `order-details-mock-tip-popover--${placement}`,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <span
        ref={anchorRef}
        className={wrapClass}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {tip.open && tip.coords && body != null
        ? createPortal(
            <span className={popoverClass} style={tip.coords} role="tooltip">
              {body}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
